import { z } from 'zod';
import {
  GameOutcomeSchema,
  GamePhaseSchema,
  GameSettingsSchema,
  RoleSchema,
  RoomStatusSchema,
  RoundHistoryEntrySchema,
  RoundResultSchema,
  StartIssueSchema,
} from './domain.js';

/**
 * Ein Spieler, wie ihn ein bestimmter Betrachter sehen darf.
 *
 * WICHTIG: `role` enthält ausschließlich **öffentlich bekannte** Rollen, also
 * die von ausgeschiedenen Spielern und nach Spielende die von allen. Die eigene
 * Rolle steht in `self`, das Wissen der Mafia über ihre Kollegen in
 * `mafiaMemberIds`. Diese eine Regel macht die Sicht auditierbar: taucht in
 * `players[].role` etwas auf, ist es für alle sichtbar — ohne Ausnahme.
 */
export const PlayerViewSchema = z.object({
  id: z.string(),
  name: z.string(),
  alive: z.boolean(),
  connected: z.boolean(),
  isHost: z.boolean(),
  role: RoleSchema.nullable(),
  /**
   * Nur ob, nie für wen — und ausschließlich für lebende Spieler. Eine Stimme
   * aus dem Jenseits (`DEAD_REDEMPTION`) würde sonst namentlich aufleuchten.
   */
  hasVoted: z.boolean(),
  hasAcknowledgedRole: z.boolean(),
  joinedAt: z.number().int(),
});
export type PlayerView = z.infer<typeof PlayerViewSchema>;

export const SelfViewSchema = z.object({
  id: z.string(),
  name: z.string(),
  role: RoleSchema.nullable(),
  alive: z.boolean(),
  isHost: z.boolean(),
  hasVoted: z.boolean(),
  hasAcknowledgedRole: z.boolean(),
  /** Nur bei Sonderrollen je `true`; nach dem Einsatz dauerhaft `false`. */
  abilityAvailable: z.boolean(),
  /**
   * Der eigene Fähigkeits-Einsatz dieser Abstimmungsrunde — Spiegelbild von
   * `ballot.yourVote` und wie dieses nur für den Handelnden selbst gefüllt.
   */
  abilityUse: z.object({ targetId: z.string().nullable() }).nullable(),
  /** Stimmberechtigt im laufenden Wahlgang; deckt die Stimme aus dem Jenseits ab. */
  canVote: z.boolean(),
});
export type SelfView = z.infer<typeof SelfViewSchema>;

export const BallotViewSchema = z.object({
  ballotNumber: z.number().int().positive(),
  isTieBreaker: z.boolean(),
  /** Wählbare Spieler. Im Tie-Breaker nur die zuvor gleichauf liegenden. */
  candidateIds: z.array(z.string()),
  votedCount: z.number().int().nonnegative(),
  eligibleCount: z.number().int().nonnegative(),
  /** Ausschließlich die eigene Stimme; `targetId === null` heißt Enthaltung. */
  yourVote: z.object({ targetId: z.string().nullable() }).nullable(),
});
export type BallotView = z.infer<typeof BallotViewSchema>;

/**
 * Der vollständige, pro Spieler projizierte Snapshot.
 *
 * Der Server sendet bei jeder Zustandsänderung genau dieses Objekt — und parst
 * es vor dem Senden durch `RoomViewSchema`. Da Zod unbekannte Felder entfernt,
 * kann ein versehentlich mitgeschlepptes Geheimnis die Verbindung nicht
 * verlassen, selbst wenn die Projektion einen Fehler hätte.
 */
export const RoomViewSchema = z.object({
  roomId: z.string(),
  roomName: z.string(),
  hostId: z.string(),
  locked: z.boolean(),
  phase: GamePhaseSchema,
  status: RoomStatusSchema,
  settings: GameSettingsSchema,
  roundNumber: z.number().int().nonnegative(),
  players: z.array(PlayerViewSchema),
  self: SelfViewSchema,
  /** Nur für Mafia-Spieler gefüllt, sonst `null`. */
  mafiaMemberIds: z.array(z.string()).nullable(),
  ballot: BallotViewSchema.nullable(),
  lastResult: RoundResultSchema.nullable(),
  tieCount: z.number().int().nonnegative(),
  outcome: GameOutcomeSchema.nullable(),
  history: z.array(RoundHistoryEntrySchema),
  phaseStartedAt: z.number().int(),
  phaseEndsAt: z.number().int().nullable(),
  /** Serverzeit des Snapshots — Basis für die Uhren-Offset-Schätzung im Client. */
  serverNow: z.number().int(),
  canStart: z.boolean(),
  startIssue: StartIssueSchema.nullable(),
  allowedMafiaCounts: z.array(z.number().int()),
  /** Obergrenze für gleichzeitig aktive Sonderrollen — Analogon zu `allowedMafiaCounts`. */
  maxSpecialRoles: z.number().int().nonnegative(),
});
export type RoomView = z.infer<typeof RoomViewSchema>;

/** Eintrag in der öffentlichen Lobby-Liste (GDD §5). */
export const PublicRoomSummarySchema = z.object({
  roomId: z.string(),
  roomName: z.string(),
  playerCount: z.number().int().nonnegative(),
  maxPlayers: z.number().int().positive(),
  mafiaCount: z.number().int().positive(),
  locked: z.boolean(),
  createdAt: z.number().int(),
});
export type PublicRoomSummary = z.infer<typeof PublicRoomSummarySchema>;

/**
 * Dramaturgie-Kanal: löst Animationen, Haptik und Töne aus.
 * Enthält grundsätzlich keine Information, die nicht ohnehin öffentlich ist.
 */
export const RoomEventSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('PHASE_CHANGED'), phase: GamePhaseSchema }),
  z.object({ type: z.literal('PLAYER_JOINED'), playerId: z.string(), name: z.string() }),
  z.object({ type: z.literal('PLAYER_LEFT'), playerId: z.string(), name: z.string() }),
  z.object({ type: z.literal('PLAYER_KICKED'), playerId: z.string(), name: z.string() }),
  z.object({ type: z.literal('HOST_CHANGED'), playerId: z.string() }),
  z.object({
    type: z.literal('VOTE_RECEIVED'),
    votedCount: z.number().int(),
    eligibleCount: z.number().int(),
  }),
  z.object({ type: z.literal('PLAYER_ELIMINATED'), playerId: z.string(), role: RoleSchema }),
  /** Ohne Ids — wer geschützt hat und wen, bleibt geheim. */
  z.object({ type: z.literal('NO_ELIMINATION'), reason: z.literal('PROTECTED') }),
  z.object({ type: z.literal('TIE'), playerIds: z.array(z.string()), tieCount: z.number().int() }),
  z.object({ type: z.literal('GAME_OVER'), outcome: GameOutcomeSchema }),
]);
export type RoomEvent = z.infer<typeof RoomEventSchema>;
